import { useEffect, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Newspaper, Plus, Trash2, ArrowUp, ArrowDown, Send, CheckCircle2, ShieldCheck,
} from 'lucide-react';
import type {
  NewsletterListRow, NewsletterDetail, NewsletterTarget, NewsletterStatus,
  NewsletterSegment, NewsletterLeadAudience, EmailBlock, MarketingCategory, NewsletterSendBody, CampaignRunRow,
} from '@evi/shared';
import { NEWSLETTER_LEAD_AUDIENCES, MARKETING_CATEGORIES, MARKETING_CATEGORY_LABELS } from '@evi/shared';
import {
  listNewslettersAdmin, getNewsletterAdmin, createNewsletterAdmin,
  updateNewsletterAdmin, approveNewsletterAdmin, sendNewsletterAdmin, getNewsletterStatusAdmin,
  listCampaignRunsAdmin, sendCampaignRemainderAdmin,
  getDisplayErrorMessage, getErrorCode,
} from '@/lib/api';
import { Modal } from '@/components/ui/Modal';
import { useRecentRoute } from '@/hooks/useRecentRoute';
import {
  RESUME_CONFIRM, msUntilSendStops, nextSendPoll, partialSummaryText, remainderConfirmText, sendOutcomeBanner,
  sendStopped, sentSummaryText, sliceConfirmText, sliceInputProblem, sliceSizeFromInput,
} from '@/lib/newsletterSendStatus';
import {
  DRAFT_CHANGED_CODE, DRAFT_CHANGED_NOTICE, approveConfirmText, draftHydrationStep, targetSummary, type ShownDraft,
} from '@/lib/newsletterApproval';
import styles from './NewsletterPage.module.css';

/**
 * Newsletter studio — compose a rich email from STRUCTURED BLOCKS, preview it as
 * the recipient will see it, review the consent-aware reach, then APPROVE and
 * SEND. This is the human side of the two-surface design: it edits the SAME
 * server-side draft the MCP newsletter_* tools edit, and its Send button calls
 * the SAME choke point (consent intersect + forced CASL footer + audit).
 *
 * Approve is the HUMAN gate — the one action no agent can perform. A draft must
 * be saved and approved here before either surface can send it.
 */

const BLOCK_TYPES: { type: EmailBlock['type']; label: string }[] = [
  { type: 'heading', label: 'Heading' },
  { type: 'paragraph', label: 'Paragraph' },
  { type: 'image', label: 'Image' },
  { type: 'image_row', label: 'Image row (2–3)' },
  { type: 'button', label: 'Button' },
  { type: 'video_poster', label: 'Video (linked poster)' },
  { type: 'list', label: 'List (bullet / numbered)' },
  { type: 'quote', label: 'Quote' },
  { type: 'offer', label: 'Offer' },
  { type: 'divider', label: 'Divider' },
];

function defaultBlock(type: EmailBlock['type']): EmailBlock {
  switch (type) {
    case 'heading': return { type: 'heading', text: 'New heading', level: 2 };
    case 'paragraph': return { type: 'paragraph', text: 'New paragraph. Use **bold**, *italic*, and http(s) links.' };
    case 'image': return { type: 'image', url: '', alt: '' };
    case 'image_row': return { type: 'image_row', images: [{ url: '', alt: '' }, { url: '', alt: '' }] };
    case 'button': return { type: 'button', label: 'Learn more', href: '' };
    case 'video_poster': return { type: 'video_poster', posterUrl: '', href: '', alt: '' };
    case 'list': return { type: 'list', style: 'bullet', items: ['First item'] };
    case 'quote': return { type: 'quote', text: 'Quoted text.' };
    case 'offer': return { type: 'offer', heading: 'Special offer', body: 'Offer details go here.' };
    case 'divider': return { type: 'divider' };
  }
}

/** A fresh draft starts with valid, immediately-saveable content. */
function starterBlocks(): EmailBlock[] {
  return [
    { type: 'heading', text: 'EV INITIATIVE — Monthly Update', level: 1 },
    { type: 'paragraph', text: 'Write your opening here.' },
  ];
}

const STATUS_LABEL: Record<NewsletterStatus, string> = {
  draft: 'Draft', approved: 'Approved', sending: 'Sending', partially_sent: 'First slice sent', sent: 'Sent',
};

/** The confirm dialog a Send opens (23 October 2026): the first slice asks a
 *  person for its size; the remainder (a newsletter's or a campaign run's)
 *  only confirms. */
type SendDialog =
  | { kind: 'slice'; id: string; resume: boolean }
  | { kind: 'remainder'; id: string; resume: boolean }
  | { kind: 'campaign_remainder'; run: CampaignRunRow };

// ── Editor state ─────────────────────────────────────────────────────────────

interface EditorState {
  id: string | null;
  status: NewsletterStatus;
  subject: string;
  /** The marketing category drivers opt in or out of. A driver who turned it
   *  off is not sent this newsletter. */
  category: MarketingCategory;
  blocks: EmailBlock[];
  target: NewsletterTarget;
}

function newEditor(): EditorState {
  return {
    id: null, status: 'draft', subject: '', category: 'company_news', blocks: starterBlocks(),
    target: { type: 'lead_audience', audience: 'newsletter' },
  };
}

function editorFromDetail(d: NewsletterDetail): EditorState {
  return {
    id: d.id, status: d.status, subject: d.subject, category: d.category ?? 'company_news',
    blocks: d.blocks, target: d.target,
  };
}

/** Plain-English names for the reasons a recipient is left out. */
const EXCLUDED_LABEL: Record<string, string> = {
  unsubscribed: 'unsubscribed',
  category_off: 'category off',
  casl_no_paid_session: 'Canada, no paid session in 2 years',
  unknown_country_no_basis: 'country unknown, no paid session in 2 years',
  frequency_cap: 'already had 2 this month',
  already_sent: 'already sent this newsletter',
  suppressed: 'suppressed',
  no_consent_record: 'not yet classified',
  internal_only: 'staging: internal only',
  no_email: 'no email',
  not_active_driver: 'not an active driver',
  duplicate_address: 'duplicate address',
  not_found: 'not found',
  no_consent_source: 'lead with no recorded consent',
  internal_lead: 'internal or test lead',
};

function excludedSummary(excluded: Partial<Record<string, number>> | undefined): string {
  if (!excluded) return '';
  return Object.entries(excluded)
    .filter(([, n]) => (n ?? 0) > 0)
    .map(([k, n]) => `${n} ${EXCLUDED_LABEL[k] ?? k}`)
    .join(', ');
}

export function NewsletterPage() {
  useRecentRoute('Newsletter');
  const qc = useQueryClient();

  const { data: list = [], isLoading, error } = useQuery({
    queryKey: ['newsletters', 'admin'],
    queryFn: listNewslettersAdmin,
    staleTime: 15_000,
  });

  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [editor, setEditor] = useState<EditorState | null>(null);
  const [dirty, setDirty] = useState(false);
  const [banner, setBanner] = useState<{ kind: 'ok' | 'err'; text: string } | null>(null);

  // Load the selected draft's full detail (blocks + reach).
  const { data: detail, dataUpdatedAt } = useQuery({
    queryKey: ['newsletter', selectedId],
    queryFn: () => getNewsletterAdmin(selectedId as string),
    enabled: !!selectedId,
  });

  // The version the reviewer was shown, which Approve sends (23 October 2026,
  // review). A detail fetched with a different version replaces it only on an
  // explicit Reload or right after this person saved (a fetch that finished
  // after acceptFrom); otherwise the page keeps what was read and says the
  // draft changed, and the Api refuses an Approve of the old version.
  const shownRef = useRef<ShownDraft | null>(null);
  const acceptFrom = useRef<number | null>(null);
  const [changedSince, setChangedSince] = useState(false);

  // When a detail loads (or reloads after save), hydrate the editor unless the
  // user has unsaved local edits or the version changed under the reviewer.
  useEffect(() => {
    if (!detail || dirty) return;
    const accept = acceptFrom.current !== null && dataUpdatedAt >= acceptFrom.current;
    if (draftHydrationStep(shownRef.current, detail, accept) === 'changed') {
      setChangedSince(true);
      return;
    }
    if (accept) acceptFrom.current = null;
    shownRef.current = { id: detail.id, version: detail.version };
    setChangedSince(false);
    setEditor(editorFromDetail(detail));
  }, [detail, dirty, dataUpdatedAt]);

  function reloadDraft() {
    if (!selectedId) return;
    acceptFrom.current = Date.now();
    setDirty(false);
    qc.invalidateQueries({ queryKey: ['newsletter', selectedId] });
  }

  const reach = detail?.reach ?? null;
  const readOnly = !!editor && editor.status !== 'draft';

  // A send runs in the background on the Api (23 October 2026): while one
  // runs, check its status every 5 s (a cheap read, no reach preview), and
  // reload the draft once it changes. A draft that opens already Sending
  // (another tab, or the agent) is watched the same way.
  const [watchingSend, setWatchingSend] = useState<string | null>(null);
  const finishedSends = useRef(new Set<string>());
  useEffect(() => {
    const id = detail?.id;
    if (detail?.status === 'sending' && id && !finishedSends.current.has(id)) setWatchingSend(id);
  }, [detail?.status, detail?.id]);
  const { data: sendStatus } = useQuery({
    queryKey: ['newsletter-status', watchingSend],
    queryFn: () => getNewsletterStatusAdmin(watchingSend as string),
    enabled: !!watchingSend,
    gcTime: 0,
    refetchInterval: (q) => nextSendPoll(q.state.data),
  });
  useEffect(() => {
    if (!watchingSend || !sendStatus || sendStatus.id !== watchingSend || sendStatus.status === 'sending') return;
    finishedSends.current.add(watchingSend);
    if (selectedId === watchingSend) setBanner(sendOutcomeBanner(sendStatus));
    qc.invalidateQueries({ queryKey: ['newsletters', 'admin'] });
    qc.invalidateQueries({ queryKey: ['newsletter', watchingSend] });
    setWatchingSend(null);
  }, [sendStatus, watchingSend, selectedId, qc]);

  function edit(patch: Partial<EditorState>) {
    setEditor((e) => (e ? { ...e, ...patch } : e));
    setDirty(true);
    setBanner(null);
  }
  function setBlocks(next: EmailBlock[]) { edit({ blocks: next }); }

  function openNew() {
    shownRef.current = null;
    acceptFrom.current = null;
    setChangedSince(false);
    setSelectedId(null);
    setEditor(newEditor());
    setDirty(true);
    setBanner(null);
  }
  function openExisting(id: string) {
    shownRef.current = null;
    acceptFrom.current = null;
    setChangedSince(false);
    setSelectedId(id);
    setDirty(false);
    setBanner(null);
  }

  const save = useMutation({
    mutationFn: async (e: EditorState) => {
      if (e.id) {
        await updateNewsletterAdmin(e.id, { subject: e.subject, category: e.category, blocks: e.blocks, target: e.target });
        return e.id;
      }
      const res = await createNewsletterAdmin({ subject: e.subject, category: e.category, blocks: e.blocks, target: e.target });
      return res.id;
    },
    onSuccess: (id) => {
      // This person's own save: the next fetch is the version they wrote.
      acceptFrom.current = Date.now();
      setDirty(false);
      setSelectedId(id);
      setBanner({ kind: 'ok', text: 'Draft saved.' });
      qc.invalidateQueries({ queryKey: ['newsletters', 'admin'] });
      qc.invalidateQueries({ queryKey: ['newsletter', id] });
    },
    onError: (e: unknown) => setBanner({ kind: 'err', text: e instanceof Error ? e.message : 'Save failed' }),
  });

  const approve = useMutation({
    // The version the page showed: the Api refuses it if the draft changed since.
    mutationFn: (v: { id: string; version: string }) => approveNewsletterAdmin(v.id, v.version),
    onSuccess: (res) => {
      setBanner({ kind: 'ok', text: 'Approved. It can now be sent (from here or the agent).' });
      qc.invalidateQueries({ queryKey: ['newsletters', 'admin'] });
      qc.invalidateQueries({ queryKey: ['newsletter', res.id] });
    },
    onError: async (e: unknown, v) => {
      const text = await getDisplayErrorMessage(e, 'Approve failed');
      if (await getErrorCode(e) === DRAFT_CHANGED_CODE) {
        // It changed since it was shown: say so; Reload shows the new version
        // for the reviewer to read before approving again.
        setDirty(false);
        setChangedSince(true);
        qc.invalidateQueries({ queryKey: ['newsletter', v.id] });
      }
      setBanner({ kind: 'err', text });
    },
  });

  const send = useMutation({
    mutationFn: (v: { id: string; body: NewsletterSendBody }) => sendNewsletterAdmin(v.id, v.body),
    onSuccess: (res) => {
      // 202: the send has claimed the draft and runs on in the background.
      setSendDialog(null);
      setBanner({
        kind: 'ok',
        text: res.max_recipients
          ? `Sending the first slice (at most ${res.max_recipients}). This page checks every 5 seconds and shows the result when it finishes; you can leave it.`
          : 'Sending. This page checks every 5 seconds and shows the result when it finishes; you can leave it.',
      });
      setEditor((e) => (e && e.id === res.campaign_id ? { ...e, status: 'sending' } : e));
      finishedSends.current.delete(res.campaign_id);
      qc.removeQueries({ queryKey: ['newsletter-status', res.campaign_id] });
      setWatchingSend(res.campaign_id);
      qc.invalidateQueries({ queryKey: ['newsletters', 'admin'] });
      qc.invalidateQueries({ queryKey: ['newsletter', res.campaign_id] });
    },
    onError: async (e: unknown, v) => {
      setSendDialog(null);
      setBanner({ kind: 'err', text: await getDisplayErrorMessage(e, 'Send failed') });
      // A refused send can have changed the draft (an approval from before
      // categories goes back to draft): show what it is now.
      qc.invalidateQueries({ queryKey: ['newsletters', 'admin'] });
      qc.invalidateQueries({ queryKey: ['newsletter', v.id] });
    },
  });

  // Campaign runs whose first slice went out (the agent's campaign_send):
  // only a person sends their remainder. The list is hidden when the Api
  // refuses it, and the remainder route refuses anyone but an operator or
  // super admin.
  const { data: partialRuns = [] } = useQuery({
    queryKey: ['campaign-runs', 'partial'],
    queryFn: () => listCampaignRunsAdmin('partial'),
    staleTime: 15_000,
    retry: false,
  });
  const sendRunRemainder = useMutation({
    mutationFn: (runId: string) => sendCampaignRemainderAdmin(runId),
    onSuccess: () => {
      setSendDialog(null);
      setBanner({ kind: 'ok', text: 'Sending the remainder of the campaign in the background.' });
      qc.invalidateQueries({ queryKey: ['campaign-runs', 'partial'] });
    },
    onError: async (e: unknown) => {
      setSendDialog(null);
      setBanner({ kind: 'err', text: await getDisplayErrorMessage(e, 'Send failed') });
      qc.invalidateQueries({ queryKey: ['campaign-runs', 'partial'] });
    },
  });

  // The size a person types for the first slice: empty until they do.
  const [sendDialog, setSendDialog] = useState<SendDialog | null>(null);
  const [sliceInput, setSliceInput] = useState('');
  function openSliceDialog(id: string, resume: boolean) {
    setSliceInput('');
    setSendDialog({ kind: 'slice', id, resume });
  }
  function confirmSendDialog() {
    if (!sendDialog) return;
    if (sendDialog.kind === 'campaign_remainder') { sendRunRemainder.mutate(sendDialog.run.run_id); return; }
    if (sendDialog.kind === 'remainder') { send.mutate({ id: sendDialog.id, body: { send_remainder: true } }); return; }
    const n = sliceSizeFromInput(sliceInput);
    if (n === null) return;
    send.mutate({ id: sendDialog.id, body: { max_recipients: n } });
  }

  function onApprove() {
    const shown = shownRef.current;
    if (!editor?.id || !shown || shown.id !== editor.id || changedSince) return;
    // What is approved is the version shown here: the Api refuses it if the
    // draft changed since (409 DRAFT_CHANGED).
    if (!window.confirm(approveConfirmText({ subject: editor.subject, target: editor.target, category: editor.category, reach: detail?.reach ?? null }))) return;
    approve.mutate({ id: shown.id, version: shown.version });
  }
  function onResume() {
    if (!editor?.id) return;
    // A stopped first slice asks for its size again; a stopped remainder only
    // confirms. Either dialog says the send stopped part way.
    if (detail?.send_phase === 'remainder') setSendDialog({ kind: 'remainder', id: editor.id, resume: true });
    else openSliceDialog(editor.id, true);
  }
  const sendingAt = sendStatus?.id === editor?.id ? (sendStatus?.sending_at ?? null) : (detail?.sending_at ?? null);
  const stopped = editor?.status === 'sending' && sendStopped({ status: 'sending', sending_at: sendingAt });
  // Re-render once when the claim turns stale, so Resume shows on a page left
  // open (nothing else re-renders it: the polls return the same data).
  const [, setStaleTick] = useState(0);
  const sendingNow = editor?.status === 'sending';
  useEffect(() => {
    if (!sendingNow || stopped) return undefined;
    const ms = msUntilSendStops(sendingAt);
    if (ms === null) return undefined;
    const timer = window.setTimeout(() => setStaleTick((n) => n + 1), ms);
    return () => window.clearTimeout(timer);
  }, [sendingNow, sendingAt, stopped]);
  function onSend() {
    if (!editor?.id) return;
    // The first send is a slice: the dialog asks a person for its size.
    openSliceDialog(editor.id, false);
  }
  function onSendRemainder() {
    if (!editor?.id) return;
    setSendDialog({ kind: 'remainder', id: editor.id, resume: false });
  }

  const canSave = !!editor && !readOnly && editor.subject.trim().length > 0 && editor.blocks.length > 0;
  const busy = save.isPending || approve.isPending || send.isPending || sendRunRemainder.isPending;
  const sliceProblem = sendDialog?.kind === 'slice' ? sliceInputProblem(sliceInput) : null;
  const sliceSize = sendDialog?.kind === 'slice' ? sliceSizeFromInput(sliceInput) : null;

  return (
    <div className={styles.page}>
      <div className={styles.header}>
        <div className={styles.headerLeft}>
          <div className={styles.headerIcon}><Newspaper size={20} /></div>
          <div>
            <h1 className={styles.title}>Newsletter</h1>
            <p className={styles.subtitle}>
              Compose from blocks, approve, and send. The unsubscribe footer and consent filtering are applied automatically.
            </p>
          </div>
        </div>
        <button className={styles.primaryBtn} onClick={openNew} type="button">
          <Plus size={15} /> New draft
        </button>
      </div>

      <div className={styles.body}>
        {/* ── List ───────────────────────────────────────────────── */}
        <aside className={styles.listCol}>
          {isLoading && <p className={styles.muted}>Loading…</p>}
          {error && <p className={styles.err}>{(error as Error).message}</p>}
          {!isLoading && list.length === 0 && <p className={styles.muted}>No newsletters yet.</p>}
          {partialRuns.length > 0 && (
            <section className={styles.runsPanel} aria-label="Campaign sends waiting for the remainder">
              <h2 className={styles.runsTitle}>Campaign sends waiting for the remainder</h2>
              <ul className={styles.list}>
                {partialRuns.map((run) => (
                  <li key={run.run_id} className={styles.runItem}>
                    <span className={styles.listSubject}>{run.subject}</span>
                    <span className={styles.listTarget}>{targetSummary(run.target as NewsletterTarget)}</span>
                    <span className={styles.muted}>{partialSummaryText(run.first_slice)}</span>
                    <button
                      type="button"
                      className={styles.sendBtn}
                      disabled={busy}
                      onClick={() => setSendDialog({ kind: 'campaign_remainder', run })}
                    >
                      <Send size={15} /> Send the remainder
                    </button>
                  </li>
                ))}
              </ul>
            </section>
          )}
          <ul className={styles.list}>
            {list.map((row: NewsletterListRow) => (
              <li key={row.id}>
                <button
                  type="button"
                  className={`${styles.listItem} ${selectedId === row.id ? styles.listItemActive : ''}`}
                  onClick={() => openExisting(row.id)}
                >
                  <span className={styles.listSubject}>{row.subject || '(no subject)'}</span>
                  <span className={styles.listMeta}>
                    <span className={`${styles.badge} ${styles[`badge_${row.status}`]}`}>{STATUS_LABEL[row.status]}</span>
                    <span className={styles.listTarget}>{targetSummary(row.target)}</span>
                    {row.plan && <span className={styles.listTarget}>Proposed by EVII ({row.plan.month})</span>}
                  </span>
                </button>
              </li>
            ))}
          </ul>
        </aside>

        {/* ── Editor + preview ───────────────────────────────────── */}
        <section className={styles.editorCol}>
          {!editor && <div className={styles.empty}>Select a newsletter, or start a new draft.</div>}

          {editor && (
            <>
              <div className={styles.editorHead}>
                <span className={`${styles.badge} ${styles[`badge_${editor.status}`]}`}>{STATUS_LABEL[editor.status]}</span>
                {detail?.plan && detail.id === editor.id && (
                  <span className={styles.reachDim} title={detail.plan.rationale}>
                    Proposed by EVII: {detail.plan.label}
                  </span>
                )}
                {reach && (
                  <span className={styles.reach}>
                    Reach: <strong>{reach.reachable}</strong> reachable
                    <span className={styles.reachDim}> · {reach.total_targeted} targeted · {reach.excluded_for_consent} excluded for consent</span>
                    {excludedSummary(reach.excluded) && (
                      <span className={styles.reachDim}> ({excludedSummary(reach.excluded)})</span>
                    )}
                  </span>
                )}
                {dirty && <span className={styles.dirtyTag}>unsaved changes</span>}
              </div>

              {banner && (
                <div className={banner.kind === 'ok' ? styles.okBanner : styles.errBanner}>{banner.text}</div>
              )}

              {changedSince && (
                <div className={styles.errBanner} role="status">
                  {DRAFT_CHANGED_NOTICE}{' '}
                  <button type="button" className={styles.saveBtn} onClick={reloadDraft}>Reload</button>
                </div>
              )}

              {readOnly && (
                <div className={styles.frozenBanner}>
                  This newsletter is <strong>{STATUS_LABEL[editor.status].toLowerCase()}</strong> and can no longer be edited.
                </div>
              )}

              <div className={styles.split}>
                {/* Form side */}
                <div className={styles.formSide}>
                  <label className={styles.field}>
                    <span className={styles.label}>Subject</span>
                    <input
                      className={styles.input}
                      value={editor.subject}
                      disabled={readOnly}
                      maxLength={200}
                      placeholder="Subject line"
                      onChange={(e) => edit({ subject: e.target.value })}
                    />
                  </label>

                  <label className={styles.field}>
                    <span className={styles.label}>Marketing category</span>
                    <select
                      className={styles.input}
                      value={editor.category}
                      disabled={readOnly}
                      onChange={(e) => edit({ category: e.target.value as MarketingCategory })}
                    >
                      {MARKETING_CATEGORIES.map((c) => <option key={c} value={c}>{MARKETING_CATEGORY_LABELS[c]}</option>)}
                    </select>
                    <span className={styles.muted}>
                      Drivers who turned this category off are not sent. Each driver gets at most 2 marketing emails a month.
                    </span>
                  </label>

                  <TargetFields
                    target={editor.target}
                    disabled={readOnly || (detail?.id === editor.id && detail?.plan?.key === 'mission_hills_investors')}
                    onChange={(target) => edit({ target })}
                  />

                  <div className={styles.blocksHead}>
                    <span className={styles.label}>Content blocks</span>
                    {!readOnly && (
                      <select
                        className={styles.addSelect}
                        value=""
                        onChange={(e) => {
                          const t = e.target.value as EmailBlock['type'];
                          if (t) setBlocks([...editor.blocks, defaultBlock(t)]);
                          e.target.value = '';
                        }}
                      >
                        <option value="">+ Add block…</option>
                        {BLOCK_TYPES.map((b) => <option key={b.type} value={b.type}>{b.label}</option>)}
                      </select>
                    )}
                  </div>

                  <div className={styles.blocks}>
                    {editor.blocks.map((block, i) => (
                      <BlockCard
                        key={i}
                        block={block}
                        index={i}
                        total={editor.blocks.length}
                        disabled={readOnly}
                        onChange={(nb) => setBlocks(editor.blocks.map((b, idx) => (idx === i ? nb : b)))}
                        onMove={(dir) => {
                          const j = i + dir;
                          if (j < 0 || j >= editor.blocks.length) return;
                          const next = [...editor.blocks];
                          [next[i], next[j]] = [next[j] as EmailBlock, next[i] as EmailBlock];
                          setBlocks(next);
                        }}
                        onRemove={() => setBlocks(editor.blocks.filter((_, idx) => idx !== i))}
                      />
                    ))}
                  </div>
                </div>

                {/* Preview side */}
                <div className={styles.previewSide}>
                  <div className={styles.previewLabel}>Preview</div>
                  <div className={styles.previewFrame}>
                    <div className={styles.previewSubject}>{editor.subject || '(no subject)'}</div>
                    <BlockPreview blocks={editor.blocks} />
                    <div className={styles.previewFooter}>
                      You are receiving this because you subscribed. Unsubscribe · EV INITIATIVE
                      <div className={styles.previewFooterNote}>The real unsubscribe footer is appended automatically on send.</div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Action bar */}
              <div className={styles.actions}>
                {!readOnly && (
                  <button
                    type="button"
                    className={styles.saveBtn}
                    disabled={!canSave || busy}
                    onClick={() => editor && save.mutate(editor)}
                  >
                    {save.isPending ? 'Saving…' : editor.id ? 'Save draft' : 'Create draft'}
                  </button>
                )}
                {editor.status === 'draft' && editor.id && (
                  <button
                    type="button"
                    className={styles.approveBtn}
                    disabled={dirty || busy || changedSince}
                    title={dirty ? 'Save your changes before approving' : changedSince ? 'Reload to review the new version first' : undefined}
                    onClick={onApprove}
                  >
                    <ShieldCheck size={15} /> {approve.isPending ? 'Approving…' : 'Approve'}
                  </button>
                )}
                {editor.status === 'approved' && editor.id && (
                  <button type="button" className={styles.sendBtn} disabled={busy} onClick={onSend}>
                    <Send size={15} /> {send.isPending ? 'Sending...' : 'Send the first slice'}
                  </button>
                )}
                {editor.status === 'partially_sent' && editor.id && (
                  <>
                    <span className={styles.sentNote}><CheckCircle2 size={15} /> {partialSummaryText(detail?.first_slice)}</span>
                    <button type="button" className={styles.sendBtn} disabled={busy} onClick={onSendRemainder}>
                      <Send size={15} /> {send.isPending ? 'Sending...' : 'Send the remainder'}
                    </button>
                  </>
                )}
                {editor.status === 'partially_sent' && detail?.send_error && (
                  <span className={styles.err}>The remainder stopped: {detail.send_error} Sending it again skips everyone already reached.</span>
                )}
                {editor.status === 'sending' && !stopped && (
                  <span className={styles.sentNote}><Send size={15} /> Sending. Checking every 5 seconds.</span>
                )}
                {editor.status === 'sending' && stopped && editor.id && (
                  <>
                    <span className={styles.err}>This send stopped part way (the Api restarted). Everyone it already reached is skipped on resume.</span>
                    <button type="button" className={styles.sendBtn} disabled={busy} onClick={onResume}>
                      <Send size={15} /> {send.isPending ? 'Resuming…' : 'Resume send'}
                    </button>
                  </>
                )}
                {editor.status === 'sent' && (
                  <span className={styles.sentNote}><CheckCircle2 size={15} /> {sentSummaryText(detail?.send_summary)}</span>
                )}
                {editor.status === 'approved' && detail?.send_error && (
                  <span className={styles.err}>The last send stopped: {detail.send_error} Sending again skips everyone it already reached.</span>
                )}
              </div>
            </>
          )}
        </section>
      </div>

      <Modal
        isOpen={sendDialog !== null}
        onClose={() => setSendDialog(null)}
        title={sendDialog?.kind === 'slice' ? 'Send the first slice' : 'Send the remainder'}
        size="sm"
        footer={(
          <>
            <button type="button" className={styles.saveBtn} onClick={() => setSendDialog(null)}>Cancel</button>
            <button
              type="button"
              className={styles.sendBtn}
              disabled={busy || (sendDialog?.kind === 'slice' && sliceSize === null)}
              onClick={confirmSendDialog}
            >
              <Send size={15} /> {sendDialog?.kind === 'slice' ? 'Send the first slice' : 'Send the remainder'}
            </button>
          </>
        )}
      >
        {sendDialog?.kind === 'slice' && (
          <form
            className={styles.sliceForm}
            onSubmit={(e) => { e.preventDefault(); confirmSendDialog(); }}
          >
            <p className={styles.dialogText}>
              The first send goes to a small slice. At most this many people are emailed now; everyone else stays
              pending until someone sends the remainder.
              {sendDialog.resume ? ' This send stopped part way: the slice counts everyone it already reached.' : ''}
            </p>
            <label className={styles.field}>
              <span className={styles.label}>Send to at most</span>
              <input
                className={styles.input}
                inputMode="numeric"
                autoFocus
                value={sliceInput}
                placeholder="Number of recipients"
                aria-describedby="slice-help"
                onChange={(e) => setSliceInput(e.target.value)}
              />
            </label>
            <p id="slice-help" className={sliceProblem && sliceInput.trim() !== '' ? styles.err : styles.muted}>
              {sliceSize !== null ? sliceConfirmText(sliceSize, reach) : sliceProblem}
            </p>
          </form>
        )}
        {sendDialog?.kind === 'remainder' && (
          <p className={styles.dialogText}>
            {sendDialog.resume ? `${RESUME_CONFIRM} ` : ''}{remainderConfirmText(detail?.first_slice, reach)}
          </p>
        )}
        {sendDialog?.kind === 'campaign_remainder' && (
          <p className={styles.dialogText}>
            {sendDialog.run.subject}: {remainderConfirmText(sendDialog.run.first_slice, null)}
          </p>
        )}
      </Modal>
    </div>
  );
}

// ── Target fields ────────────────────────────────────────────────────────────

function TargetFields(props: {
  target: NewsletterTarget;
  disabled: boolean;
  onChange: (t: NewsletterTarget) => void;
}) {
  const { target, disabled, onChange } = props;
  const seg: NewsletterSegment = target.type === 'segment' ? (target.segment ?? {}) : {};

  function setSeg(patch: Partial<NewsletterSegment>) {
    const next: NewsletterSegment = { ...seg, ...patch };
    // Drop empty keys so the summary + server see a clean segment.
    (Object.keys(next) as (keyof NewsletterSegment)[]).forEach((k) => {
      const v = next[k];
      if (v === undefined || v === '' || (Array.isArray(v) && v.length === 0)) delete next[k];
    });
    onChange({ type: 'segment', segment: next });
  }

  return (
    <div className={styles.field}>
      <span className={styles.label}>Audience</span>
      <div className={styles.radioRow}>
        <label className={styles.radio}>
          <input
            type="radio"
            name="ntype"
            checked={target.type === 'lead_audience'}
            disabled={disabled}
            onChange={() => onChange({ type: 'lead_audience', audience: 'newsletter' })}
          />
          Lead audience
        </label>
        <label className={styles.radio}>
          <input
            type="radio"
            name="ntype"
            checked={target.type === 'segment'}
            disabled={disabled}
            onChange={() => onChange({ type: 'segment', segment: {} })}
          />
          Driver segment
        </label>
      </div>

      {target.type === 'lead_audience' && (
        <select
          className={styles.input}
          value={target.audience}
          disabled={disabled}
          onChange={(e) => onChange({ type: 'lead_audience', audience: e.target.value as NewsletterLeadAudience })}
        >
          {NEWSLETTER_LEAD_AUDIENCES.map((a) => <option key={a} value={a}>{a === 'mission_hills_investors' ? 'Mission Hills investors' : a}</option>)}
        </select>
      )}

      {target.type === 'segment' && (
        <div className={styles.segGrid}>
          <label className={styles.segField}>
            <span>Fleet</span>
            <select
              disabled={disabled}
              value={seg.fleet ?? ''}
              onChange={(e) => setSeg({ fleet: (e.target.value || undefined) as NewsletterSegment['fleet'] })}
            >
              <option value="">any</option>
              <option value="member">members only</option>
              <option value="none">non-fleet only</option>
            </select>
          </label>
          <label className={styles.segField}>
            <span>Country from v1</span>
            <select
              disabled={disabled}
              value={seg.v1_place ?? ''}
              onChange={(e) => setSeg({ v1_place: (e.target.value || undefined) as NewsletterSegment['v1_place'] })}
            >
              <option value="">any driver</option>
              <option value="none">not placed by v1 (reachable before it)</option>
              <option value="station">placed by v1 stations</option>
              <option value="operator">placed by v1 operator only</option>
              <option value="any">placed by v1, either way</option>
            </select>
          </label>
          <label className={styles.segCheck}>
            <input
              type="checkbox"
              disabled={disabled}
              checked={seg.residential_owner ?? false}
              onChange={(e) => setSeg({ residential_owner: e.target.checked || undefined })}
            />
            Residential owner
          </label>
          <label className={styles.segCheck}>
            <input
              type="checkbox"
              disabled={disabled}
              checked={seg.has_payment ?? false}
              onChange={(e) => setSeg({ has_payment: e.target.checked || undefined })}
            />
            Has payment method
          </label>
          <label className={styles.segField}>
            <span>Points min</span>
            <input
              type="number"
              disabled={disabled}
              value={seg.points_min ?? ''}
              onChange={(e) => setSeg({ points_min: e.target.value === '' ? undefined : Number(e.target.value) })}
            />
          </label>
          <label className={styles.segField}>
            <span>Points max</span>
            <input
              type="number"
              disabled={disabled}
              value={seg.points_max ?? ''}
              onChange={(e) => setSeg({ points_max: e.target.value === '' ? undefined : Number(e.target.value) })}
            />
          </label>
          <label className={styles.segField}>
            <span>States (comma-sep)</span>
            <input
              disabled={disabled}
              value={(seg.geo_states ?? []).join(', ')}
              placeholder="CA, NY"
              onChange={(e) => {
                const arr = e.target.value.split(',').map((s) => s.trim()).filter(Boolean);
                setSeg({ geo_states: arr.length ? arr : undefined });
              }}
            />
          </label>
          <label className={styles.segField}>
            <span>Signed up after</span>
            <input type="date" disabled={disabled} value={seg.signup_from ?? ''} onChange={(e) => setSeg({ signup_from: e.target.value || undefined })} />
          </label>
          <label className={styles.segField}>
            <span>Signed up before</span>
            <input type="date" disabled={disabled} value={seg.signup_to ?? ''} onChange={(e) => setSeg({ signup_to: e.target.value || undefined })} />
          </label>
        </div>
      )}
    </div>
  );
}

// ── Block editor card ────────────────────────────────────────────────────────

function TextInput(props: { value: string; label: string; disabled: boolean; onChange: (v: string) => void; textarea?: boolean; placeholder?: string }) {
  return (
    <label className={styles.blockField}>
      <span>{props.label}</span>
      {props.textarea
        ? <textarea disabled={props.disabled} value={props.value} placeholder={props.placeholder} onChange={(e) => props.onChange(e.target.value)} />
        : <input disabled={props.disabled} value={props.value} placeholder={props.placeholder} onChange={(e) => props.onChange(e.target.value)} />}
    </label>
  );
}

function BlockCard(props: {
  block: EmailBlock;
  index: number;
  total: number;
  disabled: boolean;
  onChange: (b: EmailBlock) => void;
  onMove: (dir: -1 | 1) => void;
  onRemove: () => void;
}) {
  const { block, index, total, disabled, onChange, onMove, onRemove } = props;
  const patch = (p: Record<string, unknown>) => onChange({ ...block, ...p } as EmailBlock);
  const typeLabel = BLOCK_TYPES.find((t) => t.type === block.type)?.label ?? block.type;

  return (
    <div className={styles.blockCard}>
      <div className={styles.blockCardHead}>
        <span className={styles.blockType}>{typeLabel}</span>
        {!disabled && (
          <span className={styles.blockCtrls}>
            <button type="button" title="Move up" disabled={index === 0} onClick={() => onMove(-1)}><ArrowUp size={14} /></button>
            <button type="button" title="Move down" disabled={index === total - 1} onClick={() => onMove(1)}><ArrowDown size={14} /></button>
            <button type="button" title="Remove" className={styles.blockRemove} onClick={onRemove}><Trash2 size={14} /></button>
          </span>
        )}
      </div>

      {block.type === 'heading' && (
        <div className={styles.blockBody}>
          <TextInput label="Text" disabled={disabled} value={block.text} onChange={(v) => patch({ text: v })} />
          <label className={styles.blockField}>
            <span>Level</span>
            <select disabled={disabled} value={block.level ?? 2} onChange={(e) => patch({ level: Number(e.target.value) })}>
              <option value={1}>1 — section title</option>
              <option value={2}>2 — default</option>
              <option value={3}>3 — sub-heading</option>
            </select>
          </label>
        </div>
      )}

      {block.type === 'paragraph' && (
        <div className={styles.blockBody}>
          <TextInput label="Text" textarea disabled={disabled} value={block.text} onChange={(v) => patch({ text: v })} />
        </div>
      )}

      {block.type === 'image' && (
        <div className={styles.blockBody}>
          <TextInput label="Image URL (https)" disabled={disabled} value={block.url} placeholder="https://…" onChange={(v) => patch({ url: v })} />
          <TextInput label="Alt text" disabled={disabled} value={block.alt} onChange={(v) => patch({ alt: v })} />
          <TextInput label="Link URL (optional)" disabled={disabled} value={block.href ?? ''} placeholder="https://…" onChange={(v) => patch({ href: v || undefined })} />
        </div>
      )}

      {block.type === 'image_row' && (
        <div className={styles.blockBody}>
          {block.images.map((img, ii) => (
            <div key={ii} className={styles.imageRowItem}>
              <TextInput label={`Image ${ii + 1} URL`} disabled={disabled} value={img.url} placeholder="https://…"
                onChange={(v) => patch({ images: block.images.map((x, xi) => (xi === ii ? { ...x, url: v } : x)) })} />
              <TextInput label="Alt" disabled={disabled} value={img.alt}
                onChange={(v) => patch({ images: block.images.map((x, xi) => (xi === ii ? { ...x, alt: v } : x)) })} />
              {!disabled && block.images.length > 2 && (
                <button type="button" className={styles.smallRemove} onClick={() => patch({ images: block.images.filter((_, xi) => xi !== ii) })}>Remove image</button>
              )}
            </div>
          ))}
          {!disabled && block.images.length < 3 && (
            <button type="button" className={styles.smallAdd} onClick={() => patch({ images: [...block.images, { url: '', alt: '' }] })}>+ Add image</button>
          )}
        </div>
      )}

      {block.type === 'button' && (
        <div className={styles.blockBody}>
          <TextInput label="Label" disabled={disabled} value={block.label} onChange={(v) => patch({ label: v })} />
          <TextInput label="Link URL (https)" disabled={disabled} value={block.href} placeholder="https://…" onChange={(v) => patch({ href: v })} />
        </div>
      )}

      {block.type === 'video_poster' && (
        <div className={styles.blockBody}>
          <TextInput label="Poster image URL (https)" disabled={disabled} value={block.posterUrl} placeholder="https://…" onChange={(v) => patch({ posterUrl: v })} />
          <TextInput label="Video/page link (https)" disabled={disabled} value={block.href} placeholder="https://…" onChange={(v) => patch({ href: v })} />
          <TextInput label="Alt text" disabled={disabled} value={block.alt} onChange={(v) => patch({ alt: v })} />
        </div>
      )}

      {block.type === 'list' && (
        <div className={styles.blockBody}>
          <label className={styles.blockField}>
            <span>Style</span>
            <select disabled={disabled} value={block.style} onChange={(e) => patch({ style: e.target.value })}>
              <option value="bullet">Bulleted</option>
              <option value="numbered">Numbered</option>
            </select>
          </label>
          {block.items.map((it, ii) => (
            <div key={ii} className={styles.imageRowItem}>
              <TextInput label={`Item ${ii + 1}`} textarea disabled={disabled} value={it}
                onChange={(v) => patch({ items: block.items.map((x, xi) => (xi === ii ? v : x)) })} />
              {!disabled && (
                <span className={styles.blockCtrls}>
                  <button type="button" title="Move up" disabled={ii === 0}
                    onClick={() => { const n = [...block.items]; [n[ii - 1], n[ii]] = [n[ii] as string, n[ii - 1] as string]; patch({ items: n }); }}><ArrowUp size={14} /></button>
                  <button type="button" title="Move down" disabled={ii === block.items.length - 1}
                    onClick={() => { const n = [...block.items]; [n[ii], n[ii + 1]] = [n[ii + 1] as string, n[ii] as string]; patch({ items: n }); }}><ArrowDown size={14} /></button>
                  {block.items.length > 1 && (
                    <button type="button" className={styles.smallRemove} onClick={() => patch({ items: block.items.filter((_, xi) => xi !== ii) })}>Remove</button>
                  )}
                </span>
              )}
            </div>
          ))}
          {!disabled && block.items.length < 50 && (
            <button type="button" className={styles.smallAdd} onClick={() => patch({ items: [...block.items, 'New item'] })}>+ Add item</button>
          )}
        </div>
      )}

      {block.type === 'quote' && (
        <div className={styles.blockBody}>
          <TextInput label="Quote text" textarea disabled={disabled} value={block.text} onChange={(v) => patch({ text: v })} />
          <TextInput label="Attribution (optional)" disabled={disabled} value={block.attribution ?? ''} onChange={(v) => patch({ attribution: v || undefined })} />
        </div>
      )}

      {block.type === 'offer' && (
        <div className={styles.blockBody}>
          <TextInput label="Heading" disabled={disabled} value={block.heading} onChange={(v) => patch({ heading: v })} />
          <TextInput label="Body" textarea disabled={disabled} value={block.body} onChange={(v) => patch({ body: v })} />
          <TextInput label="Code (optional)" disabled={disabled} value={block.code ?? ''} onChange={(v) => patch({ code: v || undefined })} />
          <TextInput label="Button label (optional)" disabled={disabled} value={block.buttonLabel ?? ''} onChange={(v) => patch({ buttonLabel: v || undefined })} />
          <TextInput label="Button URL (optional, https)" disabled={disabled} value={block.buttonHref ?? ''} placeholder="https://…" onChange={(v) => patch({ buttonHref: v || undefined })} />
        </div>
      )}

      {block.type === 'divider' && <div className={styles.blockBody}><div className={styles.dividerPreview} /></div>}
    </div>
  );
}

// ── Recipient-side preview ───────────────────────────────────────────────────

function BlockPreview({ blocks }: { blocks: EmailBlock[] }) {
  return (
    <div className={styles.pv}>
      {blocks.map((b, i) => {
        switch (b.type) {
          case 'heading': {
            const lvl = b.level ?? 2;
            return <div key={i} className={styles[`pvH${lvl}`]}>{b.text}</div>;
          }
          case 'paragraph':
            return <p key={i} className={styles.pvP}>{b.text}</p>;
          case 'image':
            return b.url
              ? <img key={i} className={styles.pvImg} src={b.url} alt={b.alt} />
              : <div key={i} className={styles.pvImgPh}>Image: {b.alt || '(no url yet)'}</div>;
          case 'image_row':
            return (
              <div key={i} className={styles.pvRow}>
                {b.images.map((img, ii) => (
                  img.url
                    ? <img key={ii} className={styles.pvRowImg} src={img.url} alt={img.alt} />
                    : <div key={ii} className={styles.pvImgPh}>Image {ii + 1}</div>
                ))}
              </div>
            );
          case 'button':
            return <div key={i} className={styles.pvBtnWrap}><span className={styles.pvBtn}>{b.label || 'Button'}</span></div>;
          case 'video_poster':
            return b.posterUrl
              ? <div key={i} className={styles.pvVideo}><img className={styles.pvImg} src={b.posterUrl} alt={b.alt} /><span className={styles.pvPlay}>▶</span></div>
              : <div key={i} className={styles.pvImgPh}>Video poster: {b.alt || '(no url yet)'}</div>;
          case 'offer':
            return (
              <div key={i} className={styles.pvOffer}>
                <div className={styles.pvOfferH}>{b.heading}</div>
                <div className={styles.pvOfferB}>{b.body}</div>
                {b.code && <div className={styles.pvOfferCode}>Code: {b.code}</div>}
                {b.buttonLabel && <div className={styles.pvBtnWrap}><span className={styles.pvBtn}>{b.buttonLabel}</span></div>}
              </div>
            );
          case 'list':
            return b.style === 'numbered'
              ? <ol key={i} className={styles.pvList}>{b.items.map((it, ii) => <li key={ii}>{it}</li>)}</ol>
              : <ul key={i} className={styles.pvList}>{b.items.map((it, ii) => <li key={ii}>{it}</li>)}</ul>;
          case 'quote':
            return (
              <blockquote key={i} className={styles.pvQuote}>
                <span>{b.text}</span>
                {b.attribution && <cite className={styles.pvQuoteAttr}>{b.attribution}</cite>}
              </blockquote>
            );
          case 'divider':
            return <hr key={i} className={styles.pvHr} />;
          default:
            return null;
        }
      })}
    </div>
  );
}
